import { Box, Button, Flex, LinkBox, LinkOverlay, Text, useBreakpointValue } from "@chakra-ui/react"
import { useRouter } from "next/router"
import React, { useEffect, useRef, useState } from "react"
import { SparePartOverviewModel } from "../../api-clients/product-api-v1"
import { layout, pages, pdpQueryParams, sparePartsTableHeaderCodes } from "../../common/constants/constants"
import { attributes } from "../../common/constants/search-constants"
import { useBrandedTranslation } from "../../common/hooks/useBrandedTranslation"
import { FhResponseData } from "../../common/types/search"
import { flattenAttributes } from "../../common/utils/search-utils"
import { formatProductData, specialFhValue } from "../../common/utils/spare-parts-utils"
import CustomImage from "./CustomImage"
import CustomLink from "./CustomLink"
import { CMinus, CPlus } from "./Icons"
import SparePartsTable from "./SparePartsTable/SparePartsTable"

const {
  IMAGE,
  NAME,
  SYSTEMS_CATEGORY,
  YEAR_MANUFACTURE_FROM,
  YEAR_MANUFACTURE_UNTIL,
  PRODUCT_ID,
  ARTICLES_COUNT,
  SPT_DETAILS,
  SPT_SPARE_PARTS,
  SPT_MAIN_ARTICLES,
} = attributes

const { SPARE_PART_POSITION, ARTICLE_NUMBER, DESCRIPTION, REPLACEMENT_FOR, REPLACED_BY, NOTES } =
  sparePartsTableHeaderCodes

const SHOW_MORE_THRESHOLD = 3

type Props = {
  sparePart: { attribute: FhResponseData | SparePartOverviewModel }
  handleTileClick?: (event: React.MouseEvent<HTMLElement>, itemId: string, pdpLinkQueryParamsUri?: string) => void
  isFhData?: boolean
  searchTerm?: string
  areLinksExternal?: boolean // links should open in a new tab for archived articles
}

const ResultsSparePartRow = ({
  sparePart,
  handleTileClick,
  isFhData = false,
  searchTerm = "",
  areLinksExternal = false,
}: Props) => {
  const { t } = useBrandedTranslation()
  const [isExtended, setIsExtended] = useState<boolean>(false)
  const { asPath } = useRouter()

  // Reset state when spare part id in the url changes
  useEffect(() => {
    setIsExtended(false)
  }, [asPath])

  const columnHeaders = [
    {
      key: SPARE_PART_POSITION,
      value: t("spdp-POS"),
    },
    {
      key: ARTICLE_NUMBER,
      value: t("spdp-article-nubmer"),
    },
    {
      key: DESCRIPTION,
      value: t("spdp-description"),
    },
    {
      key: REPLACED_BY,
      value: t("spdp-replacedBy"),
    },
    {
      key: REPLACEMENT_FOR,
      value: t("spdp-replacementFor"),
    },
    {
      key: NOTES,
      value: t("spdp-remarks"),
    },
  ]

  const attributes = isFhData
    ? flattenAttributes(sparePart.attribute)
    : formatProductData(sparePart.attribute, columnHeaders)

  const sparePartTableRef = useRef(null)

  const articlesCount = attributes[ARTICLES_COUNT]
  const hasSparePartTable = !!attributes[SPT_DETAILS]
  const sparePartTable = hasSparePartTable ? JSON.parse(attributes[SPT_DETAILS]) : null
  const sparePartsCount = hasSparePartTable ? sparePartTable.rows.length : 0

  const sparePartsArr = attributes[SPT_SPARE_PARTS]?.split(attributes[SPT_SPARE_PARTS].includes(",") ? "," : /\s/) ?? []
  const mainArticlesArr =
    attributes[SPT_MAIN_ARTICLES]?.split(attributes[SPT_MAIN_ARTICLES].includes(",") ? "," : /\s/) ?? []

  const hasSearchTermInSparePartsTable = searchTerm && sparePartsArr.includes(searchTerm)
  const hasSearchTermInMainArticlesArr = searchTerm && mainArticlesArr.includes(searchTerm)

  const pdpLinkQueryParamsUri =
    searchTerm && (hasSearchTermInSparePartsTable || hasSearchTermInMainArticlesArr)
      ? "?" +
        new URLSearchParams({
          [pdpQueryParams.ARTICLES]: searchTerm,
          [pdpQueryParams.SEARCH_TERM]: searchTerm,
        }).toString()
      : ""

  const handleSptRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (handleTileClick) handleTileClick(e, attributes[PRODUCT_ID], pdpLinkQueryParamsUri)
  }

  const handleSparePartTableViewToggle = () => {
    setIsExtended(!isExtended)
    /* ANCHOR_NAVIGATION_HEIGHT or 48px is the height of the Anchor navigation in the PDP and SPDP,
    that needs to be offset so that the scroll to top shows the top of the table */
    const offset = isFhData ? 0 : layout.ANCHOR_NAVIGATION_HEIGHT
    if (isExtended) window.scrollTo({ top: sparePartTableRef.current.offsetTop - offset, behavior: "auto" })
  }

  const onClickPerBreakpoint = useBreakpointValue({
    base: (e: React.MouseEvent<HTMLElement>) => handleSptRowClick(e),
    sm: () => null,
  })

  return (
    <LinkBox
      display="flex"
      ref={sparePartTableRef}
      gap="6"
      py={{ base: "6", sm: "8" }}
      borderBottom="1px solid"
      _last={{ borderBottom: "none" }}
      borderColor="gray.300"
      cursor={{ base: "pointer", sm: "initial" }}
      _hover={{ "@media (hover: hover) and (pointer: fine)": { color: { base: "brand.500", sm: "black" } } }}
      _active={{ color: { base: "brand.700", sm: "black" } }}
    >
      <Box>
        <CustomImage
          src={attributes[IMAGE]}
          alt={attributes[NAME]}
          minW={{ base: "60px", sm: "162px", lg: "288px" }}
          h={{ base: "79px", sm: "214px", lg: "380px" }}
        />
      </Box>

      <Flex flexDirection="column" gap={{ base: "0", sm: "4" }} overflowX="auto" w="100%">
        <Box>
          {attributes[SYSTEMS_CATEGORY] && (
            <Text fontSize="2xs" color="gray.700" mb="2">
              {attributes[SYSTEMS_CATEGORY]}
            </Text>
          )}

          {/* Desktop and tablet */}
          <Text
            fontSize={{ base: "2xs", sm: "2xl", md: "3xl" }}
            fontWeight="bold"
            display={{ base: "none", sm: "block" }}
          >
            {attributes[NAME]}
          </Text>

          {/* Mobile */}
          <CustomLink href={`/${pages.SPARE_PART}/${attributes[PRODUCT_ID]}`}>
            <LinkOverlay
              onClick={onClickPerBreakpoint}
              onAuxClick={onClickPerBreakpoint}
              display={{ base: "block", sm: "none" }}
              isExternal={areLinksExternal}
            >
              <Text fontSize={{ base: "2xs", sm: "2xl", md: "3xl" }} fontWeight="bold">
                {attributes[NAME]}
              </Text>
            </LinkOverlay>
          </CustomLink>

          <Text fontSize={{ base: "2xs", sm: "2xl", md: "3xl" }} fontWeight="bold" mb="2">
            {specialFhValue(attributes[YEAR_MANUFACTURE_FROM])} - {specialFhValue(attributes[YEAR_MANUFACTURE_UNTIL])}
          </Text>
        </Box>

        {/* Desktop and tablet */}
        <Box display={{ base: "none", sm: "block" }}>
          <SparePartsTable
            sparePartTable={sparePartTable}
            showMoreThreshold={SHOW_MORE_THRESHOLD}
            isExtended={isExtended}
            areLinksExternal={areLinksExternal}
          />
        </Box>

        <Flex gap="6" pb="8" display={{ base: "none", sm: "flex" }}>
          {sparePartsCount > SHOW_MORE_THRESHOLD && (
            <Button
              leftIcon={isExtended ? <CMinus /> : <CPlus />}
              pl="2.5"
              border="1px solid"
              variant="secondary"
              color="black"
              minH={{ base: "11", lg: "10" }}
              onClick={handleSparePartTableViewToggle}
            >
              {t(isExtended ? "show-less" : "search-show-more-spare-parts", {
                n: sparePartsCount - SHOW_MORE_THRESHOLD,
                count: sparePartsCount > SHOW_MORE_THRESHOLD ? sparePartsCount - SHOW_MORE_THRESHOLD : sparePartsCount,
              })}
            </Button>
          )}

          <CustomLink href={`/${pages.SPARE_PART}/${attributes[PRODUCT_ID]}${pdpLinkQueryParamsUri}`}>
            <Button
              as="a"
              minH={{ base: "11", lg: "10" }}
              variant="primary"
              onClick={handleSptRowClick}
              onAuxClick={handleSptRowClick}
              target={areLinksExternal ? "_blank" : "_self"}
            >
              {t("spdp-toDetailPage")}
            </Button>
          </CustomLink>
        </Flex>

        {/* Mobile */}
        <Text fontSize="2xs" color="gray.700" display={{ base: "block", sm: "none" }}>
          {`${articlesCount} ${t("pdp-tile-article-unit", { count: +articlesCount })}`}
        </Text>
      </Flex>
    </LinkBox>
  )
}

export default ResultsSparePartRow
